import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Stack,
  IconButton,
} from '@mui/material';
import {
  AutoAwesome,
  TrendingUp,
  Psychology,
  Rocket,
  Star,
  Lightbulb,
  Close,
  PlayArrow,
} from '@mui/icons-material';

function Hero() {
  const [mounted, setMounted] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [heroImage, setHeroImage] = useState('');
  const { scrollYProgress } = useScroll();
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: true,
    initialInView: true, // Start as visible since Hero is at top of page
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.3]);

  useEffect(() => {
    const randomImageNumber = Math.floor(Math.random() * 6) + 1;
    setHeroImage(`/images/hero${randomImageNumber}-min.jpg`);
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isVideoOpen) {
        setIsVideoOpen(false);
      }
    };

    if (isVideoOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isVideoOpen]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8, rotateY: -15 },
    visible: {
      opacity: 1,
      scale: 1,
      rotateY: 0,
      transition: {
        duration: 1,
        ease: "easeOut",
      },
    },
  };

  const floatingVariants = {
    initial: { y: 0, rotate: 0 },
    animate: {
      y: [-10, 10, -10],
      rotate: [0, 5, -5, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  const pulseVariants = {
    initial: { scale: 1, opacity: 0.7 },
    animate: {
      scale: [1, 1.2, 1],
      opacity: [0.7, 1, 0.7],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  if (!mounted) return null;

  return (
    <Box className="hero-section hero-gradient">
      <div className="parallax-bg" />

      <motion.div
        style={{ y, opacity }}
        className="hero-content"
      >
        <Container maxWidth="lg" sx={{ pt: 12 }}>
          <motion.div
            ref={ref}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={containerVariants}
          >
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <motion.div variants={textVariants}>
                    <Typography
                      variant="h1"
                      sx={{
                        background: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-dark), var(--color-accent-primary))',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundSize: '200% 200%',
                        animation: 'gradientShift 4s ease infinite',
                        mb: 2,
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                      }}
                    >
                      Your career story is stellar
                    </Typography>
                  </motion.div>

                  <motion.div variants={textVariants}>
                    <Typography
                      variant="h4"
                      sx={{
                        color: 'var(--color-neutral-700)',
                        fontWeight: 400,
                        mb: 2,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: -4,
                          left: 0,
                          width: '60%',
                          height: 2,
                          background: 'linear-gradient(90deg, var(--color-accent-primary), transparent)',
                          borderRadius: 1,
                        },
                      }}
                    >
                      ...but you might not know it yet
                    </Typography>
                  </motion.div>

                  <motion.div variants={textVariants}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'var(--color-neutral-600)',
                        fontSize: '1.25rem',
                        mb: 4,
                        maxWidth: '500px',
                        lineHeight: 1.7,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          background: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-dark))',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontWeight: 700,
                        }}
                      >
                        Kazi AI
                      </Box>
                      {' '}helps you understand and tell the incredible story of your career.
                    </Typography>
                  </motion.div>

                  <motion.div variants={buttonVariants}>
                    <Stack direction="row" spacing={2}>
                      <Button
                        component={motion.a}
                        href="https://app.kaziai.me"
                        onClick={() => {
                          if (window.lintrk) {
                            window.lintrk('track', { conversion_id: 25693690 });
                          }
                        }}
                        whileHover={{
                          scale: 1.05,
                          boxShadow: '0 15px 35px rgba(255, 49, 48, 0.4)',
                        }}
                        whileTap={{ scale: 0.95 }}
                        variant="contained"
                        size="medium"
                        sx={{
                          background: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-dark))',
                          py: { xs: 1.5, sm: 2 },
                          px: { xs: 3, sm: 4 },
                          fontSize: { xs: '0.95rem', sm: '1.1rem' },
                          fontWeight: 600,
                          borderRadius: '16px',
                          boxShadow: '0 8px 25px rgba(255, 49, 48, 0.3)',
                          border: 'none',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, var(--color-brand-light), var(--color-brand-primary))',
                            opacity: 0,
                            transition: 'opacity 0.3s',
                          },
                          '&:hover::before': {
                            opacity: 1,
                          },
                        }}
                      >
                        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          Let's go!
                          <Rocket sx={{ fontSize: 20, display: { xs: 'none', sm: 'inline' } }} />
                        </Box>
                      </Button>

                      <Button
                        component={motion.button}
                        whileHover={{
                          scale: 1.05,
                          boxShadow: '0 8px 25px rgba(255, 49, 48, 0.2)',
                        }}
                        whileTap={{ scale: 0.95 }}
                        variant="outlined"
                        size="medium"
                        onClick={() => setIsVideoOpen(true)}
                        sx={{
                          borderColor: 'var(--color-brand-primary)',
                          color: 'var(--color-brand-primary)',
                          py: { xs: 1.5, sm: 2 },
                          px: { xs: 3, sm: 4 },
                          fontSize: { xs: '0.95rem', sm: '1.1rem' },
                          fontWeight: 600,
                          borderRadius: '16px',
                          borderWidth: 2,
                          background: 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            borderColor: 'var(--color-brand-dark)',
                            backgroundColor: 'rgba(255, 49, 48, 0.1)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PlayArrow sx={{ fontSize: 20, display: { xs: 'none', sm: 'inline' } }} />
                          Watch Video
                        </Box>
                      </Button>
                    </Stack>
                  </motion.div>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
                <motion.div variants={imageVariants}>
                  <Box
                    sx={{
                      position: 'relative',
                      height: 400,
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <img
                      src={heroImage}
                      alt="Professional working on career development"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '16px',
                      }}
                    />

                    {/* Floating Icons */}
                    <motion.div
                      variants={floatingVariants}
                      initial="initial"
                      animate="animate"
                      style={{
                        position: 'absolute',
                        top: 30,
                        right: 30,
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, var(--color-accent-primary), var(--color-accent-dark))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 8px 25px rgba(255, 224, 92, 0.4)',
                        }}
                      >
                        <AutoAwesome sx={{ color: 'white', fontSize: 28 }} />
                      </Box>
                    </motion.div>

                    <motion.div
                      variants={floatingVariants}
                      initial="initial"
                      animate="animate"
                      style={{
                        position: 'absolute',
                        top: 120,
                        left: 20,
                        animationDelay: '1s',
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-dark))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 8px 25px rgba(255, 49, 48, 0.4)',
                        }}
                      >
                        <Psychology sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                    </motion.div>

                    <motion.div
                      variants={floatingVariants}
                      initial="initial"
                      animate="animate"
                      style={{
                        position: 'absolute',
                        bottom: 40,
                        right: 80,
                        animationDelay: '0.5s',
                      }}
                    >
                      <Box
                        sx={{
                          width: 55,
                          height: 55,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, var(--color-brand-light), var(--color-brand-primary))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 8px 25px rgba(255, 49, 48, 0.4)',
                        }}
                      >
                        <TrendingUp sx={{ color: 'white', fontSize: 26 }} />
                      </Box>
                    </motion.div>

                    {/* Pulsing Elements */}
                    <motion.div
                      variants={pulseVariants}
                      initial="initial"
                      animate="animate"
                      style={{
                        position: 'absolute',
                        top: 60,
                        left: 60,
                      }}
                    >
                      <Star sx={{ color: 'var(--color-accent-primary)', fontSize: 20 }} />
                    </motion.div>

                    <motion.div
                      variants={pulseVariants}
                      initial="initial"
                      animate="animate"
                      style={{
                        position: 'absolute',
                        bottom: 80,
                        left: 40,
                        animationDelay: '1s',
                      }}
                    >
                      <Lightbulb sx={{ color: 'var(--color-brand-primary)', fontSize: 18 }} />
                    </motion.div>

                    {/* Gradient Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at center, transparent 40%, rgba(255, 49, 48, 0.05) 100%)',
                        pointerEvents: 'none',
                      }}
                    />
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </motion.div>

      {/* Video Overlay */}
      {isVideoOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setIsVideoOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '1200px',
              aspectRatio: '16/9',
              backgroundColor: '#000',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <IconButton
              onClick={() => setIsVideoOpen(false)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                zIndex: 10,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 49, 48, 0.8)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Close />
            </IconButton>

            {/* YouTube Video */}
            <iframe
              src="https://www.youtube.com/embed/YI74i8o-k6E?autoplay=1&rel=0&modestbranding=1"
              title="What is Kazi AI?"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </motion.div>
        </motion.div>
      )}
    </Box>
  );
}

export default Hero; 